import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const RegistrationStepperHeader = ({ title, subtitle, backPath, backLabel = 'Back', eventDetails }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Base steps
  const allSteps = [
    { label: 'Event', paths: ['/event-info'] },
    { label: 'Service', paths: ['/service'] },
    { label: 'Add-ons', paths: ['/addon'] },
    { label: 'Time Slot', paths: ['/time-slot'] },
    { label: 'Devotee', paths: ['/devotee'] },
    { label: 'Family', paths: ['/family'] },
    { label: 'Overview', paths: ['/overview'] },
    { label: 'Payment', paths: ['/event-payment', '/payment'] },
  ];

  // Filter steps based on available data
  const steps = allSteps.filter(step => {
    // Hide Family and Overview tabs completely
    if (['Family', 'Overview'].includes(step.label)) {
      return false;
    }
    
    // Always show these steps
    if (['Event', 'Service', 'Devotee', 'Payment'].includes(step.label)) {
      return true;
    }
    
    // Conditionally show Add-ons if optionalServices exist
    if (step.label === 'Add-ons') {
      const optionalServices = eventDetails?.optionalServices || [];
      return optionalServices.length > 0;
    }
    
    // Conditionally show Time Slot if timeOptions exist
    if (step.label === 'Time Slot') {
      const timeOptions = eventDetails?.timeOptions || [];
      return timeOptions.length > 0;
    }
    
    return false;
  });

  const activeIndex = Math.max(
    0,
    steps.findIndex((s) => s.paths.includes(location.pathname))
  );

  const progressPercent = steps.length > 1
    ? (activeIndex / (steps.length - 1)) * 100
    : 0;

  return (
    <div className="px-3 sm:px-4 md:px-6 pt-6 pb-4 border-b border-gray-200/60 bg-white/60 backdrop-blur-xl">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-lg sm:text-xl tracking-[0.08em] uppercase text-gray-900">
            {title}
          </h1>
          {subtitle ? (
            <div className="mt-1 text-xs text-gray-600">
              {subtitle}
            </div>
          ) : null}
        </div>

        {backPath ? (
          <button
            type="button"
            onClick={() => navigate(backPath)}
            className="shrink-0 rounded-full bg-white/80 hover:bg-white px-4 py-2 text-xs font-semibold text-gray-700 border border-gray-200 shadow-sm hover:shadow-md transition-all"
          >
            {backLabel}
          </button>
        ) : null}
      </div>

      <div className="mt-4">
        <div className="mb-3">
          <div className="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-amber-500 transition-all"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-0.5 sm:gap-1 pb-1">
          {steps.map((step, index) => {
            const isCompleted = index < activeIndex;
            const isActive = index === activeIndex;

            return (
              <React.Fragment key={step.label}>
                <div
                  className={`flex items-center gap-1 rounded-full px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs border transition-colors ${
                    isCompleted
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                      : isActive
                      ? 'bg-violet-100 text-violet-800 border-violet-200'
                      : 'bg-white/70 text-gray-500 border-gray-200'
                  }`}
                >
                  <span
                    className={`inline-flex h-4 w-4 sm:h-5 sm:w-5 items-center justify-center rounded-full text-[9px] sm:text-[10px] font-semibold border ${
                      isCompleted
                        ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                        : isActive
                        ? 'bg-violet-600 text-white border-violet-600'
                        : 'bg-white text-gray-500 border-gray-200'
                    }`}
                  >
                    {isCompleted ? <i className="fas fa-check text-[8px] sm:text-[10px]"></i> : index + 1}
                  </span>

                  <span className="whitespace-nowrap text-[10px] sm:text-[11px] hidden lg:inline">{step.label}</span>
                </div>

                {index < steps.length - 1 && (
                  <div className="h-px flex-1 max-w-2 sm:max-w-3 bg-gray-200" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RegistrationStepperHeader;
